import api from "../../../utilidades/interceptor";

const urlApi = process.env.REACT_APP_API_KEY || 'http://localhost:3000/';

/**
 * Obtiene el detalle completo de una cotizacion.
 *
 * @returns {Promise<{success: boolean, data: object}>}
 */
export const getDetailById = async (id) => {
    try {
        const response = await api.get(`${urlApi}/quotes/detail/${id}`);

        return response.data;
    } catch (error) {
        console.error('Error al obtener cotizaciones:', error);
        return { success: false, data: [] };
    }
};

/**
 * Verifica los datos principales de una cotizacion.
 *
 * @returns {Promise<{success: boolean, data: object}>}
 */
export const verifyRouteData = async (id) => {
    try {
        const response = await api.put(`${urlApi}/quotes/verify/${id}`);

        return response.data;
    } catch (error) {
        console.error('Error al obtener cotizaciones:', error);
        return { success: false, data: [] };
    }
};

/**
 * Verifica los trazos de una cotizacion.
 *
 * @returns {Promise<{success: boolean, data: object}>}
 */
export const verifyStrokesData = async (id) => {
    try {
        const response = await api.put(`${urlApi}/quotes/verify/strokes/${id}`);

        return response.data;
    } catch (error) {
        console.error('Error al obtener cotizaciones:', error);
        throw error;
    }
};

export const actualizarCostosRuta = async ({
                                               cotizacion,
                                               rutaId,
                                               pagoChofer,
                                               pagoChoferSencillo,
                                               precioCliente,
                                               pagoSubcontratista,
                                               estatus,
                                               observaciones
                                           }) => {
    try {

        const response = await api.put(`${urlApi}/quotes/route/${rutaId}/costs`, {
            cotizacion,
            pagoChofer,
            pagoChoferSencillo,
            precioCliente,
            pagoSubcontratista,
            estatus,
            observaciones
        });

        return response.data;

    } catch (error) {
        console.error('Error al actualizar costos:', error);
        throw error;
    }
};

/**
 * envío de una cotización al cliente.
 * @param idCot - ID de la cotización
 * @returns Promise
 */
export const sendToClient = async (idCot) => {
    try {

        const result = await api.post(`${urlApi}/quotes/sendEmail/${idCot}`)

        return result.data;

    } catch (error) {
        console.error('Error al enviar la cotización al cliente:', error);
        throw error;
    }
};

/**
 * Solicitar cambios.
 * @param idCot - ID de la cotización
 * @param changes - cambios a solicitar
 * @returns Promise
 */
export const requestChanges = async (idCot, changes) => {
    try {

        const result = await api.post(`${urlApi}/quotes/requestChanges/${idCot}`, changes)

        return result.data;

    } catch (error) {
        console.error('Error al enviar la cotización al cliente:', error);
        throw error;
    }
};

/**
 * Agrega una observación al hilo de seguimiento del ticket asociado a la cotización.
 * Es el mismo canal donde ya escriben inplant y operaciones — permite que la dueña
 * sume contexto sin salir de esta pantalla ni crear un campo aparte.
 *
 * NOTA: confirma con backend la ruta exacta; aquí asumo un endpoint simétrico
 * a los demás (`/quotes/ticket/:idTicket/seguimiento`). Ajusta si es distinta.
 *
 * @param idUsuario
 * @param idTicket - ID del ticket (cotizacion.idTicket)
 * @param mensaje - texto de la observación
 * @returns Promise<{success: boolean, data: object}>
 */
export const agregarSeguimientoTicket = async (idUsuario, idTicket, mensaje) => {
    try {
        const result = await api.post(`${urlApi}/quotes/ticket/${idTicket}/seguimiento`, { idUsuario, mensaje });
        return result.data;
    } catch (error) {
        console.error('Error al agregar observación al ticket:', error);
        throw error;
    }
};

/**
 * Programa la actualización automática de una cotización a partir de una fecha.
 * Si la fecha ya pasó (o es hoy), el backend ejecuta el cambio de inmediato;
 * si es futura, queda programado para ejecutarse ese día.
 *
 * @param idRuta - ID de la ruta
 * @param idUsuario - ID del usuario que programa el cambio
 * @param idCotizacion - ID de la cotización (opcional)
 * @param fechaDesde - Fecha a partir de la cual entran en vigor los cambios (YYYY-MM-DD)
 * @param fechaEjecucion - Fecha/hora en la que se debe ejecutar el proceso
 * @returns Promise<{success: boolean, data: object}>
 */
export const programarActualizacionCotizacion = async ({
                                                           idRuta,
                                                           idUsuario,
                                                           idCotizacion,
                                                           fechaDesde,
                                                           fechaEjecucion,
                                                       }) => {
    try {
        const result = await api.post(`${urlApi}/quotes/programarCotizacion`, {
            idRuta,
            idUsuario,
            idCotizacion,
            fechaDesde,
            fechaEjecucion,
        });

        return result.data;

    } catch (error) {
        console.error('Error al programar la actualización de la cotización:', error);
        throw error;
    }
};

/**
 * Obtiene los comentarios por estatus de una cotización e indica
 * cuáles puede editar el usuario actual.
 *
 * @param idCot - ID de la cotización
 * @param idUsuario - ID del usuario que consulta
 * @returns Promise<Array<{seccion, comentario, usuario, fecha, puedeEditar}>>
 */
export const getComentariosCotizacion = async (idCot, idUsuario) => {
    try {
        const result = await api.get(`${urlApi}/quotes/comments/${idCot}`, { params: { idUsuario } });
        return result.data;
    } catch (error) {
        console.error('Error al obtener los comentarios de la cotización:', error);
        throw error;
    }
};

/**
 * Guarda el comentario de una sección (NUEVO, PLANEACION, COSTEO, APROBADO).
 * Solo el usuario asignado al estatus, y el autor si ya existe, puede guardarlo.
 *
 * @param idCot - ID de la cotización
 * @param seccion - sección del comentario
 * @param comentario - texto del comentario
 * @param idUsuario - ID del usuario que escribe el comentario
 * @returns Promise
 */
export const guardarComentarioCotizacion = async (idCot, seccion, comentario, idUsuario) => {
    try {
        const result = await api.put(`${urlApi}/quotes/comments/${idCot}/${seccion}`, { idUsuario, comentario });
        return result.data;
    } catch (error) {
        console.error('Error al guardar el comentario de la cotización:', error);
        throw error;
    }
};


/**
 * Marca la cotización como precio de referencia.
 * @param idCot - ID de la cotización
 * @param idUsuario - ID del usuario que marca
 * @param motivo - motivo de la referencia
 * @returns Promise
 */
export const marcarPrecioReferencia = async (idCot, idUsuario, motivo) => {
    try {
        const result = await api.put(`${urlApi}/quotes/reference/${idCot}`, { idUsuario, motivo });
        return result.data;
    } catch (error) {
        console.error('Error al marcar la cotización como precio de referencia:', error);
        throw error;
    }
};

/**
 * Quita la marca de precio de referencia y regresa la cotización a NORMAL.
 * @param idCot - ID de la cotización
 * @returns Promise
 */
export const desmarcarPrecioReferencia = async (idCot) => {
    try {
        const result = await api.delete(`${urlApi}/quotes/reference/${idCot}`);
        return result.data;
    } catch (error) {
        console.error('Error al quitar el precio de referencia:', error);
        throw error;
    }
};

/**
 * Obtiene todas las rutas activas (de cualquier cliente).
 * @returns Promise<{success: boolean, data: Array<{id, nombre, cliente}>}>
 */
export const getRutasActivas = async () => {
    try {
        const result = await api.get(`${urlApi}/quotes/catalogs/routes/active`);
        return result.data;
    } catch (error) {
        console.error('Error al obtener las rutas activas:', error);
        throw error;
    }
};

/**
 * Marca la cotización como combinable con una ruta activa.
 * @param idCot - ID de la cotización
 * @param idRutaCombinada - ID de la ruta con la que se combina
 * @returns Promise
 */
export const marcarCombinable = async (idCot, idRutaCombinada) => {
    try {
        const result = await api.put(`${urlApi}/quotes/combinable/${idCot}`, { idRutaCombinada });
        return result.data;
    } catch (error) {
        console.error('Error al marcar la cotización como combinable:', error);
        throw error;
    }
};

/**
 * Quita la marca de combinable de la cotización.
 * @param idCot - ID de la cotización
 * @returns Promise
 */
export const desmarcarCombinable = async (idCot) => {
    try {
        const result = await api.delete(`${urlApi}/quotes/combinable/${idCot}`);
        return result.data;
    } catch (error) {
        console.error('Error al quitar la marca de combinable:', error);
        throw error;
    }
};
